import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {ClipboardList, Clock} from 'lucide-react';
import {Button, InstructionList} from '@chekinapp/ui';
import {useGuest, useIVComputedDetails, useIVRegistrationGate, usePath} from 'hooks';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useIsOtherGuest} from 'hooks/useIsOtherGuest';
import {useGetGuestId} from '../../../../hooks/useGetGuestId';
import {useReservation} from 'context/reservation';
import {useGetVerificationSteps} from './useGetVerificationSteps';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {ANALYTIC_EVENTS, Analytics, HighlightedTitle, useIVStore} from '@chekin/shared';
import {PATHS} from 'routes/constants';
import {PageLayout} from 'components/PageLayout';
import {PrivacyNote} from 'components/PrivacyNote';
import {SkipForNow} from 'components/common/SkipForNow';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {startGuestJourneyIfAbsent} from 'analytics/posthog/journey';
import {IVStepsSkeleton} from '../../components/IVStepsSkeleton';

function StartView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath, getMainPath} = usePath();
  const id = useGetGuestId();

  const handleBack = () =>
    navigate(getFullPath(PATHS.guestRegistration), {replace: true});
  const {isLoading} = useGuest({id});

  const {biomatch, isDataLoaded, underageAllowedAge} = useIVComputedDetails();
  const {isRegisterBlockedWithoutIV} = useIVRegistrationGate();
  const isOtherGuest = useIsOtherGuest();

  const {setDeclaredIvBypassAge} = useIVStore();

  const handleUnderageBypass = () => {
    setDeclaredIvBypassAge(underageAllowedAge);
    navigate(`${getMainPath(PATHS.addPersonalDataForm)}/${id || ''}`);
  };

  const {housing} = useReservation();
  const {
    isEmailEnabled,
    isPhoneEnabled,
    isLoading: isSettingsLoading,
    isNeedVerification,
  } = useVerificationSettings(housing?.id);
  const {startVerification} = useRemoteIVFlow(isNeedVerification);
  const [isStarting, setIsStarting] = useState(false);

  const handleStartVerification = async () => {
    setIsStarting(true);
    startGuestJourneyIfAbsent();
    Analytics.track(ANALYTIC_EVENTS.ivStarted, {
      type: biomatch ? 'Biomatch' : 'OCR',
    });
    try {
      await startVerification();
    } catch {
      setIsStarting(false);
    }
  };

  const verificationSteps = useGetVerificationSteps({
    biomatch,
    isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
    isEmailEnabled,
    isPhoneEnabled,
    isNeedContactsVerification: isNeedVerification,
    subject: isOtherGuest ? 'other' : 'self',
  });

  const isStepsLoading = isLoading || isSettingsLoading || !isDataLoaded;

  const stepItems = [
    ...verificationSteps.map(step => ({
      icon: step.icon,
      title: step.title ? t(step.title) : t(step.text),
      description: step.description ? t(step.description) : undefined,
    })),
    {
      icon: <ClipboardList />,
      title: t(isOtherGuest ? 'confirm_their_details' : 'confirm_your_details'),
      description: (
        <>
          <span className="lg:hidden">
            {t(
              isOtherGuest
                ? 'check_entry_form_before_sent_short_their'
                : 'check_entry_form_before_sent_short',
            )}
          </span>
          <span className="hidden lg:inline">
            {t(
              isOtherGuest
                ? 'check_entry_form_before_sent_their'
                : 'check_entry_form_before_sent',
            )}
          </span>
        </>
      ),
    },
  ];

  return (
    <PageLayout
      kicker={t('guest_registration')}
      className="iv-start-view"
      title={
        <HighlightedTitle
          i18nKey={
            isOtherGuest ? 'verify_next_guest_heading' : 'verify_your_identity_heading'
          }
        />
      }
      intro={
        <>
          <span className="lg:hidden">{t('verify_identity_intro_short')}</span>
          <span className="hidden lg:inline">{t('verify_identity_intro')}</span>
        </>
      }
      headerAddon={
        <span className="mt-2.5 inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--chekin-neutral-100)] px-3 py-[5px] text-[12px] font-medium leading-[18px] text-[var(--chekin-text-secondary)] lg:hidden">
          <Clock className="size-4 text-[var(--chekin-color-brand-blue)]" />
          {t('takes_about_2_minutes')}
        </span>
      }
      backLabel={t('summary')}
      onBack={handleBack}
      titleAction={<SkipForNow />}
      mobileTitle={t('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
      dockClassName="gap-2.5"
      dock={
        <>
          <Button
            className="w-full lg:w-[280px]"
            onClick={() => void handleStartVerification()}
            loading={isStarting}
            disabled={isLoading || isStarting}
          >
            {t('begin_verification')}
          </Button>
          {isRegisterBlockedWithoutIV && underageAllowedAge != null && (
            <Button
              onClick={handleUnderageBypass}
              disabled={isLoading}
              className="h-auto min-h-[var(--button-height-default)] w-full max-w-[340px] whitespace-normal"
              variant="outline"
            >
              {t('register_under_16_without_iv')}
            </Button>
          )}
          <PrivacyNote variant="footnote" className="lg:hidden" />
        </>
      }
    >
      <div className="flex flex-col gap-6 sm:gap-8">
        {isStepsLoading ? (
          <IVStepsSkeleton />
        ) : (
          <InstructionList numbered className="iv-start-view__steps" items={stepItems} />
        )}
      </div>
    </PageLayout>
  );
}

export {StartView};
